import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../../components/Modal';
import Button from '../../../../../components/Button';
import Input from '../../../../../components/Input';
import Dropdown from '../../../../../components/Dropdown';
import TableBox from '../../../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../../../interfaces/common';
import { buildHeadCells } from '../../../../../utils/methods';
import { VALIDATION_FIELD_MAPPING, HEAD_CELL_SORT_MAPPING } from '../../../../../utils/appConstant';
import useFetch from '../../../../../hooks/useFetch';
import useDebounce from '../../../../../hooks/useDebounce';
import useSorting from '../../../../../hooks/useSorting';
import UserService from '../../../../../services/user.service';
import PropertyService from '../../../../../services/property.service';
import { SortOrder } from '../../../../../enums';
import Checkbox from '../../../../../components/Checkbox';
import AssignPropertiesModalRow from './AssignPropertiesModalRow';

interface AssignPropertiesModalProps {
  open: boolean;
  userId?: string;
  loading: boolean;
  defaultAssignedTo?: string;
  onClose: () => void;
  onAssign: (propertyIds: string) => void;
}

const AssignPropertiesModal: React.FC<AssignPropertiesModalProps> = ({
  open, userId, loading, defaultAssignedTo, onClose, onAssign,
}) => {
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState('');
  const [assignedTo, setAssignedTo] = useState(defaultAssignedTo || '');
  const [assignedToSearch, setAssignedToSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setAssignedTo(defaultAssignedTo || '');
      setSearchText('');
      setSelectedIds([]);
      setAssignedToSearch('');
    }
  }, [open, defaultAssignedTo]);

  const debouncedSearch = useDebounce(searchText, 500);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: SortOrder.asc,
    defaultOrderBy: VALIDATION_FIELD_MAPPING.name,
  });

  const { data: properties, loading: propertiesLoading } = useFetch({
    service: PropertyService,
    func: 'list',
    payload: {
      page: 1,
      perPage: 9999,
      orderBy: (HEAD_CELL_SORT_MAPPING.propertyList as any)[orderBy] || orderBy,
      order,
      searchText: debouncedSearch.trim(),
      status: '1',
      assignedTo,
      exclude: 'users',
      exclude_id: userId,
    },
    dependency: [debouncedSearch, assignedTo, order, orderBy, userId, open],
    skip: !open,
  });

  const { data: allManagers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [open],
    skip: !open,
  });

  const { data: managers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: assignedToSearch, is_active: '1' },
    dependency: [assignedToSearch, open],
    skip: !open,
  });

  const headCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.select,
    VALIDATION_FIELD_MAPPING.name,
    VALIDATION_FIELD_MAPPING.customer,
    VALIDATION_FIELD_MAPPING.managedBy,
    VALIDATION_FIELD_MAPPING.commissionPercentage,
    VALIDATION_FIELD_MAPPING.status,
  ], 'assignPropertyModalList');

  const handleSelectToggle = useCallback((propertyId: string) => {
    setSelectedIds(prev =>
      prev.includes(propertyId)
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  }, []);

  const handleClose = useCallback(() => {
    setSearchText('');
    setAssignedTo('');
    setAssignedToSearch('');
    setSelectedIds([]);
    onClose();
  }, [onClose]);

  const handleAssign = useCallback(() => {
    if (!selectedIds.length) return;
    onAssign(selectedIds.join(','));
  }, [selectedIds, onAssign]);

  const handleSearchChange = useCallback((e: any) => {
    setSearchText(e.target.value);
  }, []);

  const handleAssignedToChange = useCallback((e: any) => {
    setAssignedTo(e.target.value);
  }, []);

  const handleAssignedToSearchChange = useCallback((e: any, _name: string) => {
    setAssignedToSearch(e.target.value);
  }, []);

  const allPropertyIds = useMemo(() => (properties || []).map((p: any) => p.id), [properties]);
  const isAllSelected = allPropertyIds.length > 0 && allPropertyIds.every((id: string) => selectedIds.includes(id));

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(prev => prev.filter(id => !allPropertyIds.includes(id)));
    } else {
      setSelectedIds(prev => [...prev, ...allPropertyIds.filter((id: string) => !prev.includes(id))]);
    }
  }, [isAllSelected, allPropertyIds]);

  const values: TableContextProvider = useMemo(() => {
    return {
      rowProps: { selectedIds, onSelectToggle: handleSelectToggle },
      row: AssignPropertiesModalRow,
      data: properties || [],
      pagination: false,
      headCells,
      order,
      orderBy,
      page: 1,
      perPage: 9999,
      total: (properties || []).length,
      loading: propertiesLoading,
      noDataFoundMessage: t('property.list.pageTexts.table.noDataFoundMessage'),
      setPage: () => {},
      setPerPage: () => {},
      setOrder,
      setOrderBy,
    };
  }, [properties, propertiesLoading, headCells, order, orderBy, selectedIds, handleSelectToggle, setOrder, setOrderBy, t]);

  const modalContent = (
    <div>
      <div className="mb-4">
        <div className="d-flex align-items-center gap-3">
          <Checkbox
            variant="primary"
            name="selectAll"
            label={t('users.addEdit.pageTexts.assignPropertiesModal.selectAll')}
            checked={isAllSelected}
            onChange={handleSelectAll}
          />
          <div className="d-flex align-items-center gap-3 ms-auto">
            <div style={{ width: 220 }}>
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t('property.list.pageTexts.filters.searchPlaceholder')}
                value={searchText}
                onChange={handleSearchChange}
                autoComplete="on"
              />
            </div>
            <div style={{ width: 220 }}>
              <Dropdown
                values={assignedTo ? [assignedTo] : []}
                options={(managers || []).map((m: any) => ({ label: m.name, value: m.id }))}
                allOptions={(allManagers || []).map((m: any) => ({ label: m.name, value: m.id }))}
                id={VALIDATION_FIELD_MAPPING.assignedTo}
                name={VALIDATION_FIELD_MAPPING.assignedTo}
                searchText={assignedToSearch}
                onSearch={handleAssignedToSearchChange}
                placeholder={t('property.list.pageTexts.filters.selectAssignedTo')}
                label={t('property.list.pageTexts.filters.assignedTo')}
                onChange={handleAssignedToChange}
              />
            </div>
          </div>
        </div>
      </div>

      <TableBox tableValues={values} />
    </div>
  );

  const modalAction = (
    <div className="d-flex gap-3">
      <Button
        className="btn btn-outline-secondary"
        text={t('users.addEdit.pageTexts.assignPropertiesModal.cancelBtn')}
        onClick={handleClose}
      />
      <Button
        className="btn btn-primary"
        text={t('users.addEdit.pageTexts.assignPropertiesModal.assignBtn')}
        disabled={!selectedIds.length || loading}
        loading={loading}
        onClick={handleAssign}
      />
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('users.addEdit.pageTexts.assignPropertiesModal.title')}
      content={modalContent}
      action={modalAction}
      maxWidth="xl"
      disableBackDrop
      titleClassName="text-start"
      actionClassName="justify-content-start"
    />
  );
};

export default AssignPropertiesModal;
